(() => {
/* Wild Gainz lab report request form.
   Progressive enhancement: the form ships hidden and this component reveals
   it, so shoppers without JavaScript see the mailto fallback instead of a
   form that cannot submit. Submissions go to Klaviyo's client API: an event
   the report delivery flow is triggered by, plus an optional list
   subscription when the consent box is ticked. */

if (customElements.get('wildgainz-report-request')) return;

const KLAVIYO_REVISION = '2026-07-15';

class WildgainzReportRequest extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-report-form]');
    this.status = this.querySelector('[data-report-status]');
    this.detail = this.querySelector('[data-report-detail]');
    if (!this.form || !this.dataset.publicKey) return;
    // The element can reconnect when an ancestor reorders the DOM (theme
    // editor section reloads, the batch grid sorting its cards), so reveal
    // and listener attachment must both be idempotent.
    if (!this.done) this.form.removeAttribute('hidden');
    if (this.bound) return;
    this.bound = true;
    this.form.addEventListener('submit', (event) => this.submit(event));
  }

  endpoint(path) {
    return (
      'https://a.klaviyo.com/client/' +
      path +
      '/?company_id=' +
      encodeURIComponent(this.dataset.publicKey)
    );
  }

  post(path, payload) {
    return fetch(this.endpoint(path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(payload),
    });
  }

  async submit(event) {
    event.preventDefault();
    if (this.busy || this.done) return;

    const data = new FormData(this.form);
    if (data.get('wg_website')) {
      // Tripped honeypot: almost certainly a bot, but fake-accept so any
      // real person caught by autofill is not left at a dead submit button.
      this.finish();
      return;
    }

    const email = String(data.get('email') || '').trim();
    const name = String(data.get('name') || '').trim();
    if (!email) return;

    // A busy flag instead of disabling the submit button: disabling would
    // silently throw keyboard focus back to the document (WCAG 2.4.3).
    this.busy = true;
    this.say(this.dataset.sendingText);

    const profileAttributes = { email: email, first_name: name };

    try {
      const response = await this.post('events', {
        data: {
          type: 'event',
          attributes: {
            properties: {
              batch_number: this.dataset.batch || '',
              flavor: this.dataset.flavor || '',
              report_number: this.dataset.reportNumber || '',
              lab_name: this.dataset.labName || '',
              report_url: this.dataset.reportUrl || '',
              source_url: window.location.href,
            },
            metric: {
              data: {
                type: 'metric',
                attributes: { name: this.dataset.metric || 'Requested Lab Report' },
              },
            },
            profile: {
              data: { type: 'profile', attributes: profileAttributes },
            },
          },
        },
      });
      if (!response.ok) throw new Error('Klaviyo event rejected');

      // Pending batches: joining the queue list is the whole point of the
      // submission, so it happens regardless of the marketing checkbox. The
      // queued batch lands on the profile for filtering inside Klaviyo.
      if (this.dataset.pending === 'true' && this.dataset.queueListId) {
        try {
          const queued = await this.post('subscriptions', {
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Lab report queue',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: Object.assign({}, profileAttributes, {
                      properties: { queued_batch: this.dataset.batch || '' },
                      subscriptions: {
                        email: { marketing: { consent: 'SUBSCRIBED' } },
                      },
                    }),
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: this.dataset.queueListId } },
              },
            },
          });
          if (!queued.ok) {
            console.warn('Wild Gainz: Klaviyo queue list add failed', queued.status);
          }
        } catch (queueError) {
          console.warn('Wild Gainz: Klaviyo queue list add failed', queueError);
        }
      }

      const autoSubscribe = this.dataset.subscribeMode === 'auto';
      if ((autoSubscribe || data.get('subscribe')) && this.dataset.listId) {
        // Consent is a bonus; a subscription hiccup must not read as a
        // failed report request, but leave a trace for debugging.
        try {
          const subscription = await this.post('subscriptions', {
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Lab report request',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: Object.assign({}, profileAttributes, {
                      subscriptions: {
                        email: { marketing: { consent: 'SUBSCRIBED' } },
                      },
                    }),
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: this.dataset.listId } },
              },
            },
          });
          if (!subscription.ok) {
            console.warn('Wild Gainz: Klaviyo list subscription failed', subscription.status);
          }
        } catch (subscribeError) {
          console.warn('Wild Gainz: Klaviyo list subscription failed', subscribeError);
        }
      }

      this.finish();
    } catch (error) {
      this.busy = false;
      this.say(this.dataset.errorText);
    }
  }

  finish() {
    this.busy = false;
    this.done = true;
    this.form.hidden = true;
    this.say(this.dataset.successText);
    // Revealed after the status text is written, so the batch line is already
    // in the element when focus moves and the whole confirmation is read out.
    if (this.detail) this.detail.hidden = false;
    if (this.status) this.status.focus();
  }

  say(text) {
    if (this.status) this.status.textContent = text || '';
  }
}

customElements.define('wildgainz-report-request', WildgainzReportRequest);
})();
